export type RoleResponse = {
  id: number;
  name: string;
  description: string;
  isActive: boolean;
  createdAt: string; // ISO date string from backend
  permissions?: PermissionResponse[];
};

export type RoleFilters = {
  term: string;
};

export type PermissionResponse = {
  id: string;
  description: string;
};

export interface MenuPermission {
  requiredPermissions: string[];
}

export type SaveRoleRequest = {
  name: string;
  description: string;
  isActive: boolean;
  permissionIds: string[];
};

export class Permission {
  id: string;
  description: string;

  constructor(id: string, description: string) {
    this.id = id;
    this.description = description;
  }

  static fromResponse(response: PermissionResponse): Permission {
    return new Permission(response.id, response.description);
  }
}

export class Role {
  id: number;
  name: string;
  description: string;
  isActive: boolean;
  createdAt: Date;
  permissions: Permission[];

  constructor(
    id: number,
    name: string,
    description: string,
    isActive: boolean,
    createdAt: Date,
    permissions: Permission[] = []
  ) {
    this.id = id;
    this.name = name;
    this.description = description;
    this.isActive = isActive;
    this.createdAt = createdAt;
    this.permissions = permissions;
  }

  static fromResponse(response: RoleResponse): Role {
    
    if (!response) {
      throw new Error('Response is null or undefined');
    }
    
    const permissions = response.permissions 
      ? response.permissions.map(p => Permission.fromResponse(p))
      : [];
    
    const role = new Role(
      response.id,
      response.name,
      response.description,
      response.isActive,
      new Date(response.createdAt),
      permissions
    );
    
    return role;
  }

  static getDisplayedColumns(): string[] {
    return ['id', 'name', 'description', 'isActive'];
  }
}

export enum PermissionType {
  AUDIT_READ = 'audits:read',
  USER_FULL = 'users:*',
  USER_READ = 'users:read',
  USER_WRITE = 'users:write',
  USER_DELETE = 'users:delete',
  USER_UPDATE = 'users:update',
  LOT_FULL = 'lots:*',
  LOT_READ = 'lots:read',
  LOT_WRITE = 'lots:write',
  LOT_DELETE = 'lots:delete',
  LOT_UPDATE = 'lots:update',
  ROLE_FULL = 'roles:*',
  ROLE_READ = 'roles:read',
  ROLE_WRITE = 'roles:write',
  ROLE_DELETE = 'roles:delete',
  ROLE_UPDATE = 'roles:update',
  AUCTION_FULL = 'auctions:*',
  AUCTION_READ = 'auctions:read',
  AUCTION_WRITE = 'auctions:write',
  AUCTION_PANEL = 'auctions:panel',
  REPORTS_FULL = 'reports:*',
  REPORTS_READ = 'reports:read',
  MARKETING_FULL = 'marketing:*',
  MARKETING_READ = 'marketing:read',
  MARKETING_WRITE = 'marketing:write',
  MARKETING_DELETE = 'marketing:delete',
  MARKETING_UPDATE = 'marketing:update',
  CONFIG_FULL = 'config:*',
  CONFIG_READ = 'config:read',
  CONFIG_WRITE = 'config:write',
  CONFIG_UPDATE = 'config:update',
  HAMMER_FULL = 'hammer:*',
  HAMMER_READ = 'hammer:read',
  BID_CUT = 'bids:cut',
  SYSTEM_USERS_FULL = 'system-users:*',
  SYSTEM_USERS_READ = 'system-users:read',
  SYSTEM_USERS_WRITE = 'system-users:write',
  SYSTEM_USERS_DELETE = 'system-users:delete',
  SYSTEM_USERS_UPDATE = 'system-users:update',
  NOTIFICATIONS_WRITE = "notifications:write",
}